import { useRef, useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { Box } from 'components/common/box';
import { Datepicker, FormGroup, UserSelector } from 'components/common/form';
import { addDays } from 'date-fns';
import { toDate } from 'date-fns-tz';
import {
  AchievementsIndividualForm,
  AchievementsIndividualUpcomingOrNot
} from '.';

import type { IndividualAchievementFormValues } from './AchievementsIndividualForm';

interface Props {
  updateData: () => void;
}

const AchievementsIndividualCreate = ({ updateData }: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: {
      accountAddForceExpirationDate,
      accountAddForceExpirationDateMaxDays,
      individualAchievementCustomAttributes,
      individualAchievementsUpcomingEnabled,
      timeZone
    },
    theme
  } = useReduxState();

  const uploadFiles = useRef<() => void>(undefined);

  const [isFutureAchievement, setIsFutureAchievement] = useState(false);
  const [newUsers, setNewUsers] = useState<string[]>([]);
  const [view, setView] = useState<'selection' | 'form'>(
    individualAchievementsUpcomingEnabled ? 'selection' : 'form'
  );

  const initialValues = {
    comments: '',
    completionDate: null,
    customAttributes: {},
    expirationDate: null,
    startedAtDate: null,
    title: '',
    userExpirationDate: null,
    workload: 0
  } as IndividualAchievementFormValues;

  if (individualAchievementCustomAttributes?.length) {
    for (const attr of individualAchievementCustomAttributes) {
      if (attr.type === 'Number') {
        initialValues.customAttributes[attr.key] = attr.min;
      }
    }
  }

  const handleHide = () => {
    setIsFutureAchievement(false);
    setNewUsers([]);
    setView(individualAchievementsUpcomingEnabled ? 'selection' : 'form');
  };

  const submit = async (values: IndividualAchievementFormValues) => {
    if (view === 'selection') {
      setView('form');
      return false;
    }

    const attachments = await uploadFiles.current();

    await api.post('/admin/achievements-individual', {
      attachments,
      comments: values.comments,
      completionDate: values.completionDate,
      customAttributes: values.customAttributes || {},
      emails: values.emails,
      expirationDate: values.expirationDate,
      startedAtDate: values.startedAtDate,
      title: values.title?.trim(),
      userExpirationDate: values.userExpirationDate
        ? toDate(values.userExpirationDate, { timeZone })
        : null,
      usernames: values.usernames,
      workload: values.workload
    });

    updateData();

    dispatch.addNotice({
      text: messages.achievementsCreateAchievementSuccess,
      type: 'success'
    });
  };

  const settings =
    view === 'selection'
      ? {
          contentStyle: { backgroundColor: 'white' },
          modalActionCategory: 'default',
          modalActionText: messages.commonContinue,
          modalWidth: 'small'
        }
      : {
          contentStyle: { backgroundColor: theme.lightGreyLighter },
          modalActionCategory: 'success',
          modalActionText: messages.commonCreate,
          modalWidth: 'wide'
        };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.achievementsIndividualCreate}
      contentStyle={settings.contentStyle}
      initialValues={initialValues}
      modalActionCategory={settings.modalActionCategory}
      modalActionText={settings.modalActionText}
      modalTitle={messages.achievementsIndividualCreate}
      modalWidth={settings.modalWidth}
      onHide={handleHide}
      onSubmit={submit}
    >
      {() => (
        <div>
          <div
            className="selection-radio"
            style={{ display: view === 'selection' ? 'block' : 'none' }}
          >
            <AchievementsIndividualUpcomingOrNot
              isFutureAchievement={isFutureAchievement}
              onSelection={setIsFutureAchievement}
            />
          </div>

          <div
            className="form"
            style={{ display: view === 'form' ? 'block' : 'none' }}
          >
            <Box>
              <FormGroup
                htmlFor="users"
                label={messages.commonUsers}
                maxWidth="100%"
              >
                <UserSelector
                  hideNewUserInformation={true}
                  onNewUsers={setNewUsers}
                  preventNewAccountsWithUsername={true}
                  required={view === 'form'}
                />
              </FormGroup>

              {accountAddForceExpirationDate && newUsers.length > 0 && (
                <FormGroup
                  htmlFor="userExpirationDate"
                  label={messages.commonExpirationDateForNewUserAccounts}
                  maxWidth="100%"
                >
                  <Datepicker
                    field="userExpirationDate"
                    id="userExpirationDate"
                    isClearable={false}
                    maxDate={addDays(
                      dateNow,
                      accountAddForceExpirationDateMaxDays
                    )}
                    minDate={dateNow}
                    required={true}
                    validateOnChange={true}
                    validateOnBlur={true}
                    style={{ maxWidth: 200 }}
                    placeholder={messages.commonExpirationDate}
                  />
                </FormGroup>
              )}
            </Box>

            <Box style={{ marginTop: 20 }}>
              <AchievementsIndividualForm
                getUploadAttachmentsHandler={(fn) => (uploadFiles.current = fn)}
                isFutureAchievement={isFutureAchievement}
                visible={view === 'form'}
              />
            </Box>
          </div>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementsIndividualCreate;
